const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const SAFE_STAGES = Object.freeze({
  access_check: {
    method: 'cognito_and_product_authorization',
    label: 'Cognito y autorización por producto',
    messages: {
      in_progress: 'Validando tu acceso.',
      completed: 'Acceso validado.',
      failed: 'No fue posible validar el acceso.'
    }
  },
  routing: {
    method: 'server_controlled_intent_routing',
    label: 'Enrutamiento controlado por el servidor',
    messages: {
      in_progress: 'Identificando la fuente adecuada.',
      completed: 'Fuente identificada.',
      failed: 'No fue posible identificar una fuente.'
    }
  },
  availability_check: {
    method: 'authorized_source_availability',
    label: 'Catálogo de fuentes autorizadas',
    messages: {
      in_progress: 'Confirmando que la fuente esté disponible.',
      completed: 'Fuente disponible.',
      blocked: 'La fuente solicitada todavía no está disponible.'
    }
  },
  parameters: {
    method: 'server_validated_parameters',
    label: 'Validación de parámetros en el servidor',
    messages: {
      in_progress: 'Validando los parámetros de la consulta.',
      completed: 'Parámetros validados.',
      blocked: 'Faltan parámetros para continuar.'
    }
  },
  analysis: {
    method: 'governed_analytics',
    label: 'Motor analítico gobernado',
    messages: {
      in_progress: 'Analizando únicamente los datos autorizados.',
      completed: 'Análisis controlado completado.',
      failed: 'No fue posible completar el análisis.'
    }
  },
  evidence_check: {
    method: 'server_side_evidence_validation',
    label: 'Validación de evidencia en el servidor',
    messages: {
      in_progress: 'Verificando evidencia y fuentes.',
      completed: 'Evidencia y fuentes verificadas.',
      failed: 'No fue posible verificar la evidencia.'
    }
  },
  response_preparation: {
    method: 'safe_answer_formatting',
    label: 'Preparación segura de la respuesta',
    messages: {
      in_progress: 'Preparando una respuesta clara.',
      completed: 'Respuesta preparada.',
      failed: 'No fue posible preparar la respuesta.'
    }
  }
});

const SAFE_STATUSES =
  new Set([
    'processing',
    'completed',
    'ok',
    'failed',
    'blocked',
    'not_available',
    'needs_clarification',
    'insufficient_evidence'
  ]);

const SAFE_STEP_STATUSES =
  new Set([
    'in_progress',
    'completed',
    'blocked',
    'failed'
  ]);

const waitForNextPoll = (
  delayMs,
  signal
) => new Promise(resolve => {
  if (signal?.aborted) {
    resolve();
    return;
  }

  const onAbort = () => {
    clearTimeout(timeoutId);
    resolve();
  };

  const timeoutId =
    setTimeout(() => {
      signal?.removeEventListener(
        'abort',
        onAbort
      );
      resolve();
    }, delayMs);

  signal?.addEventListener(
    'abort',
    onAbort,
    { once: true }
  );
});

export const createProgressId = (
  cryptoProvider = window.crypto
) => {
  if (
    cryptoProvider &&
    typeof cryptoProvider.randomUUID ===
      'function'
  ) {
    const value =
      cryptoProvider.randomUUID().toLowerCase();

    if (UUID_V4.test(value)) {
      return value;
    }
  }

  if (
    !cryptoProvider ||
    typeof cryptoProvider.getRandomValues !==
      'function'
  ) {
    throw new Error(
      'secure_random_unavailable'
    );
  }

  const bytes = new Uint8Array(16);
  cryptoProvider.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, value =>
    value.toString(16).padStart(2, '0')
  ).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20)
  ].join('-');
};

export const sanitizeProgressResponse = (
  payload,
  expectedProgressId
) => {
  const snapshot = payload?.orchestration;
  const expected =
    String(expectedProgressId || '')
      .toLowerCase();

  if (
    !UUID_V4.test(expected) ||
    snapshot?.version !== 2 ||
    snapshot?.delivery !== 'live_poll' ||
    String(snapshot?.progress_id || '')
      .toLowerCase() !== expected ||
    !SAFE_STATUSES.has(snapshot?.status) ||
    !Array.isArray(snapshot?.steps)
  ) {
    return null;
  }

  const steps = snapshot.steps
    .slice(0, 12)
    .map(step => {
      const definition =
        SAFE_STAGES[step?.stage];

      if (
        !definition ||
        definition.method !== step?.method ||
        !SAFE_STEP_STATUSES.has(step?.status) ||
        definition.messages[step?.status] !==
          step?.message
      ) {
        return null;
      }

      const elapsed = Number(step.elapsed_ms);

      return {
        stage: step.stage,
        status: step.status,
        elapsed_ms:
          Number.isFinite(elapsed)
            ? Math.max(
                0,
                Math.min(
                  Math.trunc(elapsed),
                  300000
                )
              )
            : 0,
        message: step.message,
        method: step.method,
        method_label: definition.label
      };
    })
    .filter(Boolean);

  return {
    version: 2,
    delivery: 'live_poll',
    progress_id: expected,
    status: snapshot.status,
    steps
  };
};

export const pollOrchestrationProgress = async ({
  url,
  token,
  progressId,
  signal,
  onProgress,
  fetchImpl = fetch,
  intervalMs = 800,
  maxDurationMs = 60000
}) => {
  const startedAt = Date.now();

  while (
    !signal?.aborted &&
    Date.now() - startedAt < maxDurationMs
  ) {
    try {
      const response = await fetchImpl(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Authorization:
              `Bearer ${token}`
          },
          body: JSON.stringify({
            progress_id: progressId
          }),
          cache: 'no-store',
          signal
        }
      );

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        return;
      }

      if (response.ok) {
        const body = await response
          .json()
          .catch(() => null);

        const safeSnapshot =
          sanitizeProgressResponse(
            body,
            progressId
          );

        if (
          safeSnapshot &&
          !signal?.aborted
        ) {
          onProgress(safeSnapshot);
        }
      }
    } catch (error) {
      if (
        signal?.aborted ||
        error?.name === 'AbortError'
      ) {
        return;
      }
    }

    await waitForNextPoll(
      intervalMs,
      signal
    );
  }
};
